import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { apiManager } from "../../services/apiManager";
import { outpatientAPI, syncVisitServicesToVaccinations, clinicAppointmentsAPI } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import SOAPDetailsContent from "./SOAPDetailsContent";
import AttachmentsModal from "../modals/AttachmentsModal";
import MedicalHistoryModal from "../modals/MedicalHistoryModal";
import CheckoutModal from "../modals/CheckoutModal";
import DashboardTab from "./OutpatientDetails/sections/DashboardTab";
import MedicalHistoryTab from "./OutpatientDetails/sections/MedicalHistoryTab";
import AttachmentsTab from "./OutpatientDetails/sections/AttachmentsTab";
import "../../pages/reception/ReceptionStyles.css";
import "./OutpatientDetailsOverlay.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";

const OutpatientDetailsOverlay = ({
  isVisible,
  outpatientId,
  onClose,
  workingHours = [],
  fetchAllTabData,
  onSOAPOpenChange
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? "ar-SA" : "en-US";
  const fallbackLabels = useMemo(
    () => ({
      petGuardian: t("receptionOutpatients.tab.fallback.petGuardian"),
      pet: t("receptionOutpatients.tab.fallback.pet"),
      vet: t("receptionOutpatients.tab.fallback.vet"),
      notAvailable: t("receptionOutpatients.details.fallback.na"),
    }),
    [t]
  );

  // Helper function to resolve pet profile image URL
  const getPetImageUrl = useCallback((pet) => {
    if (!pet) return null;

    // Handle profile_image_path which might be a string or an object
    const profilePath = typeof pet.profile_image_path === "string"
      ? pet.profile_image_path
      : pet.profile_image_path?.url || pet.profile_image_path?.path;

    // Try multiple possible image fields
    const imageUrl =
      pet.primaryImageUrl ||
      pet.primary_image_url ||
      profilePath ||
      pet.profile_image_url ||
      pet.profileImageUrl ||
      pet.image ||
      pet.avatar;

    // If we have a URL, check if it needs the API base URL
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim()) {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      // Check if it's already a full URL
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      // If it's a relative path, prepend API URL
      const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      return `${API_BASE_URL}${cleanPath}`;
    }

    return null;
  }, []);

  const formatMoney = useCallback(
    (value) => {
      const number = Number(value);
      if (Number.isNaN(number)) return fallbackLabels.notAvailable;
      return number.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    [locale, fallbackLabels.notAvailable]
  );
  const [overlayData, setOverlayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  const petImageUrl = useMemo(() => getPetImageUrl(overlayData?.pet), [overlayData?.pet, getPetImageUrl]);
  const petInitial = overlayData?.pet?.name?.charAt(0) || 'P';

  // Header attribute line — same formatters as the Summary tab's Pet card
  const petAgeDisplay = useMemo(() => {
    const raw = overlayData?.pet?.age;
    if (raw === null || raw === undefined || raw === '') return fallbackLabels.notAvailable;

    const toYears = (n) => t('common.ageYears', { count: n, defaultValue: `${n} years` });
    const toMonths = (n) => t('common.ageMonths', { count: n, defaultValue: `${n} months` });

    if (typeof raw === 'number' && Number.isFinite(raw)) return toYears(Math.max(0, Math.round(raw)));

    const s = String(raw).trim();
    const n = parseFloat(s.replace(/[^\d.]/g, ''));
    if (Number.isNaN(n)) return s || fallbackLabels.notAvailable;

    const lower = s.toLowerCase();
    const hasYear = lower.includes('year') || lower.includes('years') || lower.includes('سنة') || lower.includes('سنوات');
    const hasMonth = lower.includes('month') || lower.includes('months') || lower.includes('شهر') || lower.includes('أشهر');

    if (hasYear) return toYears(Math.max(0, Math.round(n)));
    if (hasMonth) return toMonths(Math.max(0, Math.round(n)));

    return toYears(Math.max(0, Math.round(n)));
  }, [overlayData?.pet?.age, fallbackLabels.notAvailable, t]);

  const petGenderDisplay = useMemo(() => {
    const raw = overlayData?.pet?.gender;
    if (raw === null || raw === undefined || raw === '') return fallbackLabels.notAvailable;
    const s = String(raw).trim();
    const k = s.toLowerCase();
    const normalized = (k === 'm' || k === 'male') ? 'male' : (k === 'f' || k === 'female') ? 'female' : null;
    if (!normalized) return s;
    return t(`completeProfile.gender.options.${normalized}`, { defaultValue: s });
  }, [overlayData?.pet?.gender, fallbackLabels.notAvailable, t]);

  const petAttributesLine = useMemo(() => {
    const pet = overlayData?.pet;
    if (!pet) return '';
    return [
      pet.species?.name || fallbackLabels.notAvailable,
      pet.breed?.name || fallbackLabels.notAvailable,
      petGenderDisplay,
      petAgeDisplay,
      pet.neutered ? t('common.yes', { defaultValue: 'Yes' }) : t('common.no', { defaultValue: 'No' }),
    ].join(' · ');
  }, [overlayData?.pet, petGenderDisplay, petAgeDisplay, fallbackLabels.notAvailable, t]);

  // Reset image error when pet changes
  useEffect(() => {
    setImageError(false);
  }, [overlayData?.pet?.id, petImageUrl]);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState('summary');

  // Service search states
  const [showServiceSearch, setShowServiceSearch] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [savingAll, setSavingAll] = useState(false);

  // Context menu states
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedServiceId, setSelectedServiceId] = useState(null);


  // SOAP modal states
  const [showSOAPModal, setShowSOAPModal] = useState(false);

  // Attachments modal state
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);

  // Medical history modal state
  const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState(false);

  // Checkout modal state
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutInitializing, setCheckoutInitializing] = useState(false);

  // Checkout form state
  const [checkoutForm, setCheckoutForm] = useState({
    finalDiagnosis: '',
    doctorNotes: '',
    prescription: '',
    followUpRequired: false,
    followUpDate: '',
    followUpTime: '',
    followUpType: '',
    initialDiagnosis: ''
  });

  // Working hours states
  const [fetchedWorkingHours, setFetchedWorkingHours] = useState([]);
  const [loadingWorkingHours, setLoadingWorkingHours] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // Refs for Flatpickr
  const dateInputRef = useRef(null);
  const datePickerRef = useRef(null);

  // Check if visit is completed (must be declared before effects that use it)
  const isVisitCompleted = overlayData?.status === 'completed';
  // Check if visit is in checkout or closed status
  const isCheckout = overlayData?.status?.toLowerCase() === 'checkout' || overlayData?.status?.toLowerCase() === 'closed';

  // Use refs to track if we've already fetched to prevent duplicate calls
  const hasFetchedDetailsRef = useRef(false);
  const hasFetchedWorkingHoursRef = useRef(false);

  useEffect(() => {
    if (isVisible && outpatientId) {
      // Only fetch if we haven't fetched for this visit yet
      if (!hasFetchedDetailsRef.current) {
        fetchOutpatientDetails();
        hasFetchedDetailsRef.current = true;
      }
      if (!hasFetchedWorkingHoursRef.current) {
        fetchWorkingHours();
        hasFetchedWorkingHoursRef.current = true;
      }
    } else {
      // Reset refs when overlay closes or visit changes
      hasFetchedDetailsRef.current = false;
      hasFetchedWorkingHoursRef.current = false;
    }
  }, [isVisible, outpatientId]);


  const fetchOutpatientDetails = async () => {
    if (!outpatientId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data) {
          let visitData = result.data;

          // Note: SOAP notes are now fetched separately by SOAPDetailsContent component
          // to avoid duplicate API calls. Initial diagnosis will be loaded from SOAP
          // when the SOAP component mounts.

          setOverlayData(visitData);

          // Load existing visit details as selected services
          if (result.data.visit_details && result.data.visit_details.length > 0) {
            const existingServices = result.data.visit_details.map(visitDetail => {
              // Find the corresponding clinic service if available
              const clinicService = result.data.clinic?.services?.find(service =>
                service.id === visitDetail.clinic_service.id
              );

              // Prefer values from visit_detail.clinic_service with fallback to clinic.services
              const detailService = visitDetail.clinic_service || {};
              const fallbackName = `Service #${detailService.id || ''}`;

              const nameEn = detailService.service_name_en || clinicService?.service_name_en || fallbackName;
              const nameAr = detailService.service_name_ar || clinicService?.service_name_ar || fallbackName;
              const serviceCode = (clinicService?.service_code ?? detailService.service_code) ?? "-";

              const discountPercentageRaw = (detailService.discount_percentage ?? clinicService?.discount_percentage);
              const discountPercentage = discountPercentageRaw != null ? parseFloat(discountPercentageRaw) : null;

              const hasDueDate = (typeof detailService.has_due_date === 'boolean')
                ? detailService.has_due_date
                : (clinicService?.has_due_date ?? null);

              const hasVat = (typeof detailService.has_vat === 'boolean')
                ? detailService.has_vat
                : (clinicService?.has_vat ?? false);

              const vatPercentage = detailService.vat_percentage != null
                ? parseFloat(detailService.vat_percentage)
                : (clinicService?.vat_percentage != null ? parseFloat(clinicService.vat_percentage) : null);

              const serviceType = clinicService?.service_type || detailService.service_type || '';

              return {
                id: (detailService.id != null ? detailService.id : clinicService?.id),
                service_code: serviceCode,
                service_name_en: nameEn,
                service_name_ar: nameAr,
                discount_percentage: discountPercentage,
                has_due_date: hasDueDate,
                has_vat: hasVat,
                vat_percentage: vatPercentage,
                service_type: serviceType,
                service_list_id: detailService.service_list_id ?? clinicService?.service_list_id ?? null,
                medication_category: detailService.medication_category ?? clinicService?.medication_category ?? null,
                price: parseFloat(visitDetail.price),
                quantity: parseInt(visitDetail.qty),
                discount: parseFloat(visitDetail.discount),
                notes: visitDetail.notes || '',
                total: parseFloat(visitDetail.total_amount),
                due_date: visitDetail.due_date || null,
                outpatient_visit_detail_id: visitDetail.id
              };
            });

            setSelectedServices(existingServices);
          }

          // Pre-populate checkout form with existing data if visit is completed
          if (result.data.status === 'completed') {
            setCheckoutForm({
              finalDiagnosis: result.data.final_diag || '',
              initialDiagnosis: result.data.initial_diag || '',
              doctorNotes: result.data.doctor_notes || '',
              prescription: result.data.prescription || '',
              followUpRequired: result.data.follow_up_required || false,
              followUpDate: result.data.follow_up_date ? result.data.follow_up_date.split(' ')[0] : '',
              followUpTime: result.data.follow_up_time || '',
              followUpType: result.data.follow_up_type || ''
            });
          }
        } else {
          const fallbackMessage = t("receptionOutpatients.details.toasts.loadFailed");
          setError(result.message || fallbackMessage);
          showErrorToast(result.message || fallbackMessage);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const fallbackMessage = t("receptionOutpatients.details.toasts.loadFailed");
        const message = errorData.message || response.statusText || fallbackMessage;
        setError(message);
        showErrorToast(message);
      }
    } catch (error) {
      console.error('Error fetching outpatient details:', error);
      const errorMessage = error.statusText || t("receptionOutpatients.details.toasts.loadFailed");
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkingHours = async () => {
    setLoadingWorkingHours(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/clinic-working-hours`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data && result.data.working_hours) {
          // Transform the API response to match the expected format
          const transformedWorkingHours = result.data.working_hours.map(wh => ({
            day: wh.day || wh.day.toLowerCase(),
            from: wh.open_time,
            to: wh.close_time,
            enabled: !wh.is_closed
          }));
          setFetchedWorkingHours(transformedWorkingHours);

          // Generate time slots for all working hours
          const slots = generateTimeSlotsFromWorkingHours(transformedWorkingHours);
          setAvailableTimeSlots(slots);
        } else {
          console.error('Failed to fetch working hours:', result.message);
          showErrorToast(result.message || t("receptionOutpatients.details.toasts.workingHoursFailed"));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching working hours:', errorData.message || response.statusText);
        showErrorToast(errorData.message || response.statusText || t("receptionOutpatients.details.toasts.workingHoursFailed"));
      }
    } catch (error) {
      console.error('Error fetching working hours:', error);
      showErrorToast(t("receptionOutpatients.details.toasts.workingHoursFailed"));
    } finally {
      setLoadingWorkingHours(false);
    }
  };

  // Generate time slots from working hours
  const generateTimeSlotsFromWorkingHours = (workingHours) => {
    if (!workingHours || workingHours.length === 0) return [];

    const slots = [];
    workingHours.forEach(wh => {
      if (wh.enabled) {
        const startTime = wh.from;
        const endTime = wh.to;

        // Convert time strings to minutes for easier calculation
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        // Generate 30-minute slots
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
          const timeString = minutesToTime(minutes);
          slots.push({
            time: timeString,
            display: timeString,
            day: wh.day
          });
        }
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  // Get available time slots for selected date
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return [];
    }

    const selectedDateObj = new Date(selectedDate);
    const dayOfWeek = selectedDateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Convert day number to day name
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];

    // Find working hours for the selected day
    const dayWorkingHours = fetchedWorkingHours.find(wh =>
      wh.day && wh.day.toLowerCase() === dayName && wh.enabled
    );

    if (!dayWorkingHours) {
      return [];
    }

    const slots = [];
    const startTime = dayWorkingHours.from;
    const endTime = dayWorkingHours.to;

    // Convert time strings to minutes for easier calculation
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    // Get current time
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const isToday = selectedDateObj.toDateString() === now.toDateString();

    // Generate 30-minute slots
    for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
      const timeString = minutesToTime(minutes);

      // Skip past times if it's today (with 15-minute buffer)
      if (isToday && minutes <= (currentTime + 15)) {
        continue;
      }

      slots.push({
        time: timeString,
        display: timeString,
        day: dayName
      });
    }

    return slots;
  };

  const handleClose = () => {
    setOverlayData(null);
    setError(null);
    setShowServiceSearch(false);
    setServiceSearchQuery('');
    setSelectedServices([]);
    setShowContextMenu(false);
    setSelectedServiceId(null);
    setShowCheckoutModal(false);
    setShowSOAPModal(false);
    setShowAttachmentsModal(false);
    setShowTimeDropdown(false);
    setAvailableTimeSlots([]);
    setFetchedWorkingHours([]);
    setActiveTab('summary');
    onClose();
  };

  // Service search functions
  const filteredServices = overlayData?.clinic?.services?.filter(service => {
    if (!serviceSearchQuery) return true;

    const query = serviceSearchQuery.toLowerCase();

    const nameEn = (service.service_name_en ?? '').toString().toLowerCase();
    const nameAr = (service.service_name_ar ?? '').toString().toLowerCase();
    const code = (service.service_code ?? '').toString().toLowerCase();
    const type = (service.service_type ?? '').toString().toLowerCase();
    const hasDue = (
      typeof service.has_due_date === 'boolean'
        ? (service.has_due_date ? 'yes' : 'no')
        : (service.has_due_date == null ? '' : String(service.has_due_date))
    ).toLowerCase();

    return (
      nameEn.includes(query) ||
      nameAr.includes(query) ||
      code.includes(query) ||
      type.includes(query) ||
      hasDue.includes(query)
    );
  }) || [];

  const handleServiceSelect = (service) => {
    if (!outpatientId) {
      showErrorToast(t("receptionOutpatients.details.toasts.noVisitId"));
      return;
    }


    // Check if service is already selected
    const existingService = selectedServices.find(s => s.id === service.id);
    if (existingService) {
      showErrorToast(t("receptionOutpatients.details.toasts.serviceAlreadyAdded"));
      setShowServiceSearch(false);
      setServiceSearchQuery('');

      // Scroll to and highlight the existing service
      setTimeout(() => {
        const serviceRow = document.querySelector(`[data-service-id="${service.id}"]`);
        if (serviceRow) {
          serviceRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          serviceRow.classList.add('highlight-row');
          setTimeout(() => {
            serviceRow.classList.remove('highlight-row');
          }, 2000);
        }
      }, 300);

      return;
    }

    // Add the service to the local state without calling API
    const serviceWithQuantity = {
      ...service,
      service_list_id: service.service_list_id ?? null,
      medication_category: service.medication_category ?? null,
      quantity: 1,
      discount: null,
      notes: '',
      discount_percentage: service.discount_percentage != null ? parseFloat(service.discount_percentage) : null,
      outpatient_visit_detail_id: null, // No visit detail ID since it's not saved yet
      total: parseFloat(service.price) * 1 // No discount applied initially
    };

    setSelectedServices(prev => [...prev, serviceWithQuantity]);
    setShowServiceSearch(false);
    setServiceSearchQuery('');

    showSuccessToast(t("receptionOutpatients.details.toasts.serviceAdded"));
  };

  const handleServiceRemove = (serviceId) => {
    const service = selectedServices.find(s => s.id === serviceId);

    if (!service) {
      showErrorToast(t("receptionOutpatients.details.toasts.serviceNotFound"));
      return;
    }

    // Remove service from local state only (API call will happen during bulk save)
    setSelectedServices(prev => prev.filter(s => s.id !== serviceId));
    showSuccessToast(t("receptionOutpatients.details.toasts.serviceRemoved"));
  };

  const handleServiceUpdate = (serviceId, updatedData) => {
    const service = selectedServices.find(s => s.id === serviceId);

    if (!service) {
      showErrorToast(t("receptionOutpatients.details.toasts.serviceNotFound"));
      return;
    }

    // Update local state only (API call will happen during bulk save)
    setSelectedServices(prev =>
      prev.map(s => {
        if (s.id === serviceId) {
          const quantity = updatedData.quantity === '' ? 1 : (updatedData.quantity || s.quantity);
          const price = updatedData.price || s.price;
          const discount = updatedData.discount !== undefined ? updatedData.discount : s.discount;
          const subtotal = quantity * price;
          const total = subtotal - discount;
          return { ...s, ...updatedData, total: Math.max(0, total) };
        }
        return s;
      })
    );
  };

  // Update local state only; saving occurs via "Save & Exit"
  const debouncedUpdate = (serviceId, updatedData) => {
    setSelectedServices(prev =>
      prev.map(s => {
        if (s.id === serviceId) {
          const quantity = updatedData.quantity === '' ? 1 : (updatedData.quantity || s.quantity);
          const price = updatedData.price || s.price;
          const discount = updatedData.discount !== undefined ? updatedData.discount : s.discount;
          const subtotal = quantity * price;
          const total = subtotal - discount;
          return { ...s, ...updatedData, total: Math.max(0, total) };
        }
        return s;
      })
    );
  };

  const handleQuantityChange = (serviceId, newQuantity) => {
    if (newQuantity === '') {
      debouncedUpdate(serviceId, { quantity: '' });
      return;
    }
    const quantity = parseInt(newQuantity);
    if (!isNaN(quantity)) {
      debouncedUpdate(serviceId, { quantity });
    }
  };

  const handleDiscountChange = (serviceId, newDiscount) => {
    let discount = parseFloat(newDiscount);
    const originalDiscount = discount;
    if (isNaN(discount) || discount < 0) {
      discount = null;
    }

    const svc = selectedServices.find(s => s.id === serviceId);
    const quantity = svc ? svc.quantity : 1;
    const price = svc ? svc.price : 0;
    const subtotal = quantity * price;
    const percentCap = (svc && typeof svc.discount_percentage === 'number' && !isNaN(svc.discount_percentage))
      ? svc.discount_percentage
      : null;
    const maxDiscountAmount = percentCap != null ? (subtotal * percentCap) / 100 : subtotal;

    if (discount > maxDiscountAmount) {
      discount = maxDiscountAmount;
    }

    // Also ensure we never discount more than subtotal
    if (discount > subtotal) {
      discount = subtotal;
    }

    // Notify user if entered value exceeded the allowed maximum
    if (!isNaN(originalDiscount) && originalDiscount > discount) {
      const maxValue = (Math.round((percentCap != null ? maxDiscountAmount : subtotal) * 100) / 100).toFixed(2);
      showErrorToast(t('receptionOutpatients.details.toasts.discountCap', {
        cap: percentCap != null ? t('receptionOutpatients.details.toasts.discountCapPercent', { percent: percentCap }) : t('receptionOutpatients.details.toasts.discountCapSubtotal'),
        maxValue
      }));
    }

    debouncedUpdate(serviceId, { discount });
  };

  const handleNotesChange = (serviceId, newNotes) => {
    debouncedUpdate(serviceId, { notes: newNotes });
  };

  const handleDueDateChange = (serviceId, newDueDate) => {
    if (isVisitCompleted) return;
    debouncedUpdate(serviceId, { due_date: newDueDate });
  };

  const isArabicText = (text) => /[\u0600-\u06FF]/.test(text || '');


  const handleInitialDiagnosisChange = (newValue) => {
    if (!outpatientId) {
      showErrorToast(t("receptionOutpatients.details.toasts.noVisitId"));
      return;
    }

    // Update local state immediately for better UX
    setOverlayData(prev => prev ? { ...prev, initial_diag: newValue } : prev);

  };



  const handleContextMenuDelete = () => {
    if (selectedServiceId) {
      handleServiceRemove(selectedServiceId);
    }
    setShowContextMenu(false);
    setSelectedServiceId(null);
  };

  const handleContextMenuClose = () => {
    setShowContextMenu(false);
    setSelectedServiceId(null);
  };


  // Initialize Flatpickr for due date inputs
  useEffect(() => {
    if (!isVisible || isVisitCompleted) return;

    // Delay to ensure inputs are rendered before initializing
    const timerId = setTimeout(() => {
      const dueDateInputs = document.querySelectorAll('.due-date-input');
      dueDateInputs.forEach(input => {
        if (input.disabled) return;
        if (!input.hasAttribute('data-flatpickr-initialized')) {
          input.setAttribute('data-flatpickr-initialized', 'true');

          const serviceId = input.getAttribute('data-service-id');
          const flatpickrInstance = flatpickr(input, {
            dateFormat: "Y-m-d",
            enableTime: false,
            minDate: "today",
            onChange: function (selectedDates, dateStr) {
              if (serviceId) {
                handleDueDateChange(parseInt(serviceId, 10), dateStr);
              }
            }
          });

          // Store the instance for cleanup
          input._flatpickrInstance = flatpickrInstance;
        }
      });
    }, 0);

    return () => {
      clearTimeout(timerId);
      // Cleanup flatpickr instances
      const dueDateInputs = document.querySelectorAll('.due-date-input');
      dueDateInputs.forEach(input => {
        if (input._flatpickrInstance) {
          input._flatpickrInstance.destroy();
          input._flatpickrInstance = null;
          input.removeAttribute('data-flatpickr-initialized');
        }
      });
    };
  }, [selectedServices, isVisible, isVisitCompleted]);

  // Initialize Flatpickr for date picker
  useEffect(() => {
    if (showCheckoutModal && dateInputRef.current) {
      // Initialize date picker
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }

      // Get available days from working hours
      const availableDays = fetchedWorkingHours.map(wh => {
        const dayMapping = {
          'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
          'thursday': 4, 'friday': 5, 'saturday': 6
        };
        return dayMapping[wh.day];
      }).filter(day => day !== undefined);

      // Create disable function for dates
      const disableFunction = availableDays.length > 0 ? [
        function (date) {
          const dayOfWeek = date.getDay();
          return !availableDays.includes(dayOfWeek);
        },
        function (date) {
          // Disable past dates
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ] : [
        function (date) {
          // Disable past dates when no working hours are set
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ];

      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          handleCheckoutFormChange('followUpDate', dateStr);
        }
      });
    }

    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, [showCheckoutModal, fetchedWorkingHours]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTimeDropdown && !event.target.closest('.time-dropdown-container')) {
        setShowTimeDropdown(false);
      }
    };

    if (showTimeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTimeDropdown]);

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Generate time slots based on working hours for a specific day
  const generateTimeSlots = (workingHours, selectedDay = null) => {
    if (!workingHours || workingHours.length === 0) {
      // Default time slots if no working hours
      return [
        '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'
      ];
    }

    const slots = [];

    // If a specific day is selected, filter by that day
    if (selectedDay !== null) {
      const today = new Date();
      const selectedDate = new Date(today.getFullYear(), today.getMonth(), selectedDay);
      const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

      // Convert day number to day name
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[dayOfWeek];

      // Find working hours for the selected day
      const dayWorkingHours = workingHours.find(wh =>
        wh.day && wh.day.toLowerCase() === dayName && wh.enabled
      );

      if (dayWorkingHours) {
        const startTime = dayWorkingHours.from;
        const endTime = dayWorkingHours.to;

        // Convert time strings to minutes for easier calculation
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        // Get current time
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const isToday = selectedDay === now.getDate();

        // Generate 30-minute slots
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
          const timeString = minutesToTime(minutes);

          // Skip past times if it's today (with 15-minute buffer like Add New Outpatient)
          if (isToday && minutes <= (currentTime + 15)) {
            continue;
          }

          slots.push(timeString);
        }
      }
    } else {
      // If no specific day, show all working hours
      workingHours.forEach(wh => {
        if (wh.enabled) {
          const startTime = wh.from;
          const endTime = wh.to;

          // Convert time strings to minutes for easier calculation
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = timeToMinutes(endTime);

          // Generate 30-minute slots
          for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
            const timeString = minutesToTime(minutes);
            if (!slots.includes(timeString)) {
              slots.push(timeString);
            }
          }
        }
      });
    }

    return slots.sort((a, b) => a.localeCompare(b));
  };






  const handleCheckoutCancel = () => {
    setShowCheckoutModal(false);
    setShowTimeDropdown(false);
  };


  // SOAP modal handlers
  const handleSOAPClick = () => {
    setActiveTab('soap');
  };

  const handleSOAPClose = () => {
    setActiveTab('summary');
  };

  const handleSOAPSave = (soapData) => {
    // You can add additional logic here if needed
  };

  // Update visit's initial diagnosis from SOAP
  const handleInitialDiagnosisUpdate = async (initialDiagnosis) => {
    if (!outpatientId) {
      return;
    }

    try {
      // Build payload with updated initial diagnosis and existing services
      const payload = buildVisitBulkPayload(initialDiagnosis);

      // If there are services, use bulk API
      if (selectedServices && selectedServices.length > 0) {
        // Update via bulk API
        await outpatientAPI.addServiceToOutpatientVisit(payload);
      } else {
        // If no services, update visit directly using updateOutpatientVisit
        await outpatientAPI.updateOutpatientVisit(outpatientId, {
          initial_diag: initialDiagnosis
        });
      }

      // Update local state to reflect the change
      setOverlayData(prev => prev ? { ...prev, initial_diag: initialDiagnosis } : prev);

      // Refresh overlay data to ensure we have the latest from the server
      await fetchOutpatientDetails();
    } catch (error) {
      console.error('Error updating initial diagnosis:', error);
      throw error; // Re-throw to let SOAP component handle it
    }
  };

  // Notify parent when SOAP opens/closes
  useEffect(() => {
    if (onSOAPOpenChange) {
      onSOAPOpenChange(showSOAPModal, showSOAPModal ? handleSOAPClose : null);
    }
  }, [showSOAPModal, onSOAPOpenChange]);

  // Attachments modal handlers
  const handleAttachmentsClick = () => {
    setActiveTab('attachments');
  };

  // Checkout modal handlers
  const handleCheckoutClick = () => {
    // Open modal immediately and show initializing state while API runs
    if (overlayData) {
      setCheckoutForm({
        finalDiagnosis: overlayData.final_diag || '',
        doctorNotes: overlayData.doctor_notes || '',
        prescription: overlayData.prescription || '',
        followUpRequired: overlayData.follow_up_required || false,
        followUpDate: overlayData.follow_up_date || '',
        followUpTime: overlayData.follow_up_time || '',
        followUpType: overlayData.follow_up_type || '',
        initialDiagnosis: overlayData.initial_diag || ''
      });
    }
    setCheckoutInitializing(true);
    setShowCheckoutModal(true);

    // Save any unsaved service changes and initial diagnosis using the same bulk API
    (async () => {
      try {
        if (!outpatientId) return;
        const payload = buildVisitBulkPayload();

        // Send all changes in one bulk request - backend will delete all and insert new
        await outpatientAPI.addServiceToOutpatientVisit(payload);

        // // Refresh parent data to reflect latest totals before checkout
        // if (fetchAllTabData) {
        //   await fetchAllTabData(true);
        // }
      } catch (e) {
        // Non-blocking: keep modal open even if pre-save fails
        console.error('Pre-checkout save failed:', e);
      } finally {
        setCheckoutInitializing(false);
      }
    })();
  };

  const handleCheckoutFormChange = (field, value) => {
    setCheckoutForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckoutSave = async (checkoutData) => {
    try {
      // Merge the form data from modal with parent state
      const finalCheckoutData = { ...checkoutForm, ...checkoutData };

      // Here you would implement the actual checkout API call

      // Reset form after successful checkout
      setCheckoutForm({
        finalDiagnosis: '',
        doctorNotes: '',
        prescription: '',
        followUpRequired: false,
        followUpDate: '',
        followUpTime: '',
        followUpType: ''
      });

      showSuccessToast(t("receptionOutpatients.details.toasts.checkoutSuccess"));

      // Create follow-up appointment if required
      if (finalCheckoutData.follow_up_required && finalCheckoutData.follow_up_date && finalCheckoutData.follow_up_time) {
        try {
          const appointmentPayload = {
            pet_id: overlayData?.pet?.id,
            clinic_id: overlayData?.clinic_id || overlayData?.clinic?.id,
            vet_id: overlayData?.vet_id || overlayData?.vet?.id || null,
            appointment_at: `${finalCheckoutData.follow_up_date} ${finalCheckoutData.follow_up_time}:00`,
            pet_owner_id: overlayData?.pet_owner_id || overlayData?.pet_owner?.id,
            reason: `Follow-up after outpatient visit`,
            appointment_type_id: finalCheckoutData.follow_up_type || '3',
          };
          await clinicAppointmentsAPI.createAppointment(appointmentPayload);
          showSuccessToast(t("receptionOutpatients.details.toasts.followUpAppointmentCreated"));
        } catch (apptError) {
          console.error('Error creating follow-up appointment:', apptError);
          showErrorToast(t("receptionOutpatients.details.toasts.followUpAppointmentFailed"));
        }
      }

      // Sync services with due dates into pet vaccination records
      const petId = overlayData?.pet?.id;
      if (petId && selectedServices.length > 0) {
        await syncVisitServicesToVaccinations(petId, selectedServices, overlayData?.visit_date);
      }

      // Refresh the tab data from parent
      if (fetchAllTabData) {
        await fetchAllTabData(true);
      }

      // Close the checkout modal and the entire overlay to exit the case view
      setShowCheckoutModal(false);
      handleClose();
    } catch (error) {
      console.error('Error during checkout:', error);
      throw error;
    }
  };

  const handleAttachmentsSave = () => {
    // Optional: refresh any related data if needed
  };

  // Build payload for bulk save by outpatient visit ID
  const buildVisitBulkPayload = (initialDiagOverride = null) => {
    // All services have the same structure for backend to handle delete-all and insert
    const allServices = selectedServices.map(svc => ({
      outpatient_visit_id: parseInt(outpatientId),
      clinic_service_id: svc.id,
      qty: svc.quantity,
      price: svc.price,
      discount: svc.discount,
      notes: svc.notes,
      due_date: svc.due_date || null
    }));

    return {
      initial_diag: initialDiagOverride !== null ? initialDiagOverride : (overlayData?.initial_diag || ''),
      new_services: allServices
    };
  };

  // Save all changes (services and initial diagnosis) then exit
  const handleSaveAllAndExit = async () => {
    if (!outpatientId) {
      showErrorToast(t("receptionOutpatients.details.toasts.noVisitId"));
      return;
    }

    setSavingAll(true);
    try {
      const payload = buildVisitBulkPayload();

      // Send all changes in one bulk request - backend will delete all and insert new
      await outpatientAPI.addServiceToOutpatientVisit(payload);

      showSuccessToast(t("receptionOutpatients.details.toasts.saveSuccess"));
      if (fetchAllTabData) {
        await fetchAllTabData(true);
      }
      handleClose();
    } catch (e) {
      console.error('Error saving all changes:', e);
      showErrorToast(t("receptionOutpatients.details.toasts.saveFailed"));
    } finally {
      setSavingAll(false);
    }
  };

  // Medical history modal handlers
  const handleEMRClick = () => {
    setActiveTab('emr');
  };

  // Compute grand total for all selected services
  const computeServiceTotal = (svc) => {
    const quantity = Number(svc?.quantity) || 0;
    const price = Number(svc?.price) || 0;
    const discount = Number(svc?.discount) || 0;
    const subtotal = quantity * price;
    const total = subtotal - discount;
    return total > 0 ? total : 0;
  };

  const grandTotal = Array.isArray(selectedServices)
    ? selectedServices.reduce((sum, svc) => {
      const lineTotal = typeof svc.total === 'number' ? svc.total : computeServiceTotal(svc);
      return sum + (isNaN(lineTotal) ? 0 : lineTotal);
    }, 0)
    : 0;

  if (!isVisible) return null;

  return (
    <>
      {/* Service Search Modal - Rendered outside main overlay */}
      {showServiceSearch && (
        <div
          className="service-search-modal"
          dir={i18n.dir()}
          lang={i18n.language}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowServiceSearch(false);
              setServiceSearchQuery('');
            }
          }}
        >
          <div className="service-search-content">
            <div className="service-search-header">
              <h4>{t("receptionOutpatients.details.services.search.title")}</h4>
              <button
                className="close-search-btn"
                onClick={() => {
                  setShowServiceSearch(false);
                  setServiceSearchQuery('');
                }}
              >
                ×
              </button>
            </div>
            <div className="service-search-input">
              <input
                type="text"
                placeholder={t("receptionOutpatients.details.services.search.placeholder")}
                value={serviceSearchQuery}
                onChange={(e) => setServiceSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="service-search-results">
              {filteredServices.length > 0 ? (
                filteredServices.map((service) => (
                  <div
                    key={service.id}
                    className="service-search-item"
                    onClick={() => handleServiceSelect(service)}
                    style={{
                      opacity: 1,
                      cursor: 'pointer',
                      pointerEvents: 'auto'
                    }}
                  >
                    <div className="service-item-info">
                      <div className="service-name">
                        {service.service_name_en || service.service_name_ar}
                      </div>
                      <div className="service-details">
                        <span className="service-code">{service.service_code || fallbackLabels.notAvailable}</span>
                        <span className="service-type">{service.service_type || fallbackLabels.notAvailable}</span>
                        <span className="service-type">
                          {service.has_due_date
                            ? t("receptionOutpatients.details.services.dueDate.yes")
                            : t("receptionOutpatients.details.services.dueDate.no")}
                        </span>
                        <span className="service-price">{formatMoney(service.price)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-services-found">
                  {t("receptionOutpatients.details.services.search.empty")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="context-menu-overlay"
          onClick={handleContextMenuClose}
        >
          <div
            className="context-menu"
            style={{
              left: contextMenuPosition.x,
              top: contextMenuPosition.y
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="context-menu-item" onClick={handleContextMenuDelete}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M10 11v6M14 11v6" />
              </svg>
              {t("receptionOutpatients.details.services.context.delete")}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        outpatientId={outpatientId}
        onSave={handleCheckoutSave}
        checkoutForm={checkoutForm}
        onFormChange={handleCheckoutFormChange}
        workingHours={fetchedWorkingHours}
        initializing={checkoutInitializing}
      />

      {/* Main Outpatient Details Modal */}
      <div className={`outpatient-modal-overlay ${isVisible ? 'active' : ''}`} onClick={handleClose}>
        <div className="outpatient-modal-container" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="outpatient-modal-header">
            <div className="outpatient-header-info">
              <div className="outpatient-pet-avatar-mini">
                {petImageUrl && !imageError ? (
                  <img
                    src={petImageUrl}
                    alt={overlayData?.pet?.name || 'Pet'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px'
                    }}
                    onError={() => {
                      setImageError(true);
                    }}
                  />
                ) : (
                  <div style={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {petInitial}
                  </div>
                )}
              </div>
              <div className="outpatient-header-text">
                <h2>{overlayData?.pet?.name || t('common.loading')}</h2>
                {petAttributesLine && (
                  <p className="outpatient-header-sub" title={petAttributesLine}>{petAttributesLine}</p>
                )}
              </div>
            </div>
            <div className="header-actions">
              <button className="outpatient-close-modal-btn" onClick={handleClose}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="outpatient-modal-body">
            <div className="outpatient-modal-sidebar">
              <nav>
                <button
                  className={`outpatient-nav-item ${activeTab === 'summary' ? 'active' : ''}`}
                  onClick={() => setActiveTab('summary')}
                >
                  <i className="fas fa-th-large"></i>
                  <span>{t('receptionOutpatients.details.tabs.summary', { defaultValue: 'Summary' })}</span>
                </button>
                <button
                  className={`outpatient-nav-item ${activeTab === 'soap' ? 'active' : ''}`}
                  onClick={() => setActiveTab('soap')}
                >
                  <i className="fas fa-notes-medical"></i>
                  <span>{t('receptionOutpatients.details.tabs.soap', { defaultValue: 'SOAP Notes' })}</span>
                </button>
                <button
                  className={`outpatient-nav-item ${activeTab === 'emr' ? 'active' : ''}`}
                  onClick={() => setActiveTab('emr')}
                >
                  <i className="fas fa-file-medical"></i>
                  <span>{t('receptionOutpatients.details.tabs.emr', { defaultValue: 'Medical History' })}</span>
                </button>
                <button
                  className={`outpatient-nav-item ${activeTab === 'services' ? 'active' : ''}`}
                  onClick={() => setActiveTab('services')}
                >
                  <i className="fas fa-concierge-bell"></i>
                  <span>{t('receptionOutpatients.details.tabs.services', { defaultValue: 'Services' })}</span>
                </button>
                <button
                  className={`outpatient-nav-item ${activeTab === 'attachments' ? 'active' : ''}`}
                  onClick={() => setActiveTab('attachments')}
                >
                  <i className="fas fa-paperclip"></i>
                  <span>{t('receptionOutpatients.details.tabs.attachments', { defaultValue: 'Attachments' })}</span>
                </button>
              </nav>

              {!isCheckout && (
                <div className="outpatient-sidebar-bottom">
                  <button
                    type="button"
                    className={`outpatient-nav-item outpatient-nav-checkout ${showCheckoutModal ? 'active' : ''}`}
                    onClick={handleCheckoutClick}
                    disabled={isVisitCompleted || !selectedServices || selectedServices.length === 0}
                  >
                    <i className="fas fa-check-double"></i>
                    <span>{isVisitCompleted ? t('receptionOutpatients.details.buttons.completed') : t('receptionOutpatients.details.buttons.checkout')}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="outpatient-modal-main-content">
              {loading ? (
                <div className="skel-content" aria-busy="true">
                  <div className="visit-skel skel-block h20 w30" />
                  <div className="visit-skel skel-block card" />
                  <div className="visit-skel skel-block w90" />
                  <div className="visit-skel skel-block w70" />
                  <div className="visit-skel skel-block w50" />
                  <div className="visit-skel skel-block card" />
                </div>
              ) : error ? (
                <div className="outpatient-error-state">
                  <p>{error}</p>
                  <button onClick={fetchOutpatientDetails}>{t('receptionOutpatients.details.retry')}</button>
                </div>
              ) : (
                <div className="tab-pane-content">
                  {/* Summary Tab */}
                  {activeTab === 'summary' && overlayData && (
                    <DashboardTab data={overlayData} t={t} />
                  )}

                  {/* SOAP Tab */}
                  {activeTab === 'soap' && (
                    <div className="soap-tab-integrated">
                      <SOAPDetailsContent
                        isVisible={true}
                        outpatientId={outpatientId}
                        onSave={handleSOAPSave}
                        finalDiagnosis={overlayData?.final_diag || ''}
                        visitInfo={{
                          serial: overlayData?.out_serial_num || '',
                          petName: overlayData?.pet?.name || '',
                          ownerName: overlayData?.pet_owner?.full_name || '',
                          visitDate: overlayData?.visit_date || ''
                        }}
                        services={selectedServices.map(svc => ({
                          id: svc.id,
                          service_name_en: svc.service_name_en,
                          service_name_ar: svc.service_name_ar,
                          quantity: svc.quantity,
                          notes: svc.notes || ''
                        }))}
                        onInitialDiagnosisUpdate={handleInitialDiagnosisUpdate}
                        visitInitialDiagnosis={overlayData?.initial_diag || ''}
                        readOnly={isCheckout}
                        hideHeader={true}
                        petId={overlayData?.pet?.id}
                      />
                    </div>
                  )}

                  {/* EMR Tab */}
                  {activeTab === 'emr' && (
                    <MedicalHistoryTab petId={overlayData?.pet?.id} />
                  )}

                  {/* Services Tab */}
                  {activeTab === 'services' && (
                    <div className="outpatient-tab-content">
                      <div className="outpatient-services-header">
                        <div className="header-left">
                          <i className="fas fa-concierge-bell main-icon"></i>
                          <h3>{t('receptionOutpatients.details.services.title')}</h3>
                        </div>
                        <button
                          className="outpatient-add-service-btn"
                          onClick={() => setShowServiceSearch(true)}
                          disabled={isVisitCompleted || isCheckout}
                        >
                          <i className="fas fa-plus"></i>
                          {t('receptionOutpatients.details.services.add')}
                        </button>
                      </div>

                      <div className="outpatient-services-table-wrapper">
                        <table className="outpatient-services-table">
                          <thead>
                            <tr>
                              <th style={{ width: '10%' }}>{t('receptionOutpatients.details.services.table.code')}</th>
                              <th style={{ width: '20%' }}>{t('receptionOutpatients.details.services.table.name')}</th>
                              <th style={{ width: '8%' }}>{t('receptionOutpatients.details.services.table.quantity')}</th>
                              <th style={{ width: '8%' }}>{t('receptionOutpatients.details.services.table.price')}</th>
                              <th style={{ width: '8%' }}>{t('receptionOutpatients.details.services.table.discount')}</th>
                              <th style={{ width: '8%' }}>{t('receptionOutpatients.details.services.table.total')}</th>
                              <th style={{ width: '16%' }}>{t('receptionOutpatients.details.services.table.dueDate')}</th>
                              <th style={{ width: '25%' }}>{t('receptionOutpatients.details.services.table.notes')}</th>
                              <th style={{ width: '3%' }}>{t('receptionOutpatients.details.services.table.actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedServices.length > 0 ? (
                              selectedServices.map((service) => (
                                <tr
                                  key={service.id}
                                  className="service-row"
                                  data-service-id={service.id}
                                >
                                  <td>{service.service_code || fallbackLabels.notAvailable}</td>
                                  <td>{service.service_name_en || service.service_name_ar || fallbackLabels.notAvailable}</td>
                                  <td>
                                    <input
                                      type="number"
                                      min="1"
                                      value={service.quantity}
                                      onChange={(e) => !(isVisitCompleted || isCheckout) && handleQuantityChange(service.id, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={isVisitCompleted || isCheckout}
                                    />
                                  </td>
                                  <td>{formatMoney(service.price)}</td>
                                  <td>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={service.discount ?? ''}
                                      onChange={(e) => !(isVisitCompleted || isCheckout) && handleDiscountChange(service.id, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={isVisitCompleted || isCheckout}
                                    />
                                  </td>
                                  <td>{formatMoney(service.total)}</td>
                                  <td>
                                    {service.has_due_date ? (
                                      <input
                                        type="text"
                                        className="due-date-input flatpickr-input"
                                        value={service.due_date || ''}
                                        readOnly
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={isVisitCompleted}
                                        data-service-id={service.id}
                                        placeholder={t('receptionOutpatients.details.services.placeholders.dueDate')}
                                      />
                                    ) : (
                                      <span>{service.due_date || t('receptionOutpatients.details.services.dueDate.no')}</span>
                                    )}
                                  </td>
                                  <td>
                                    <textarea
                                      placeholder={t('receptionOutpatients.details.services.placeholders.notes')}
                                      rows="1"
                                      value={service.notes || ''}
                                      onChange={(e) => !(isVisitCompleted || isCheckout) && handleNotesChange(service.id, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={isVisitCompleted || isCheckout}
                                    />
                                  </td>
                                  <td>
                                    <button
                                      className="action-btn remove-service-btn"
                                      onClick={() => !(isVisitCompleted || isCheckout) && handleServiceRemove(service.id)}
                                      disabled={isVisitCompleted || isCheckout}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                  <div className="outpatient-empty-state">
                                    <i className="fas fa-inbox" style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.3 }}></i>
                                    <p>{t('receptionOutpatients.details.services.empty')}</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Attachments Tab */}
                  {activeTab === 'attachments' && (
                    <AttachmentsTab outpatientId={outpatientId} readOnly={isCheckout} />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          {overlayData && !loading && !error && (
            <div className="outpatient-modal-footer">
              <div className="outpatient-total-summary">
                <span className="outpatient-total-label">{t('receptionOutpatients.details.summary.totalLabel')}:</span>
                <span className="outpatient-total-value">{formatMoney(grandTotal)}</span>
              </div>
              <div className="outpatient-footer-btns">
                {/* Cancel removed — the header × closes the modal.
                    Save hides entirely on completed / checkout visits,
                    where editing is locked anyway. */}
                {!isCheckout && overlayData?.status !== 'completed' && overlayData?.status !== 'checkout' && (
                  <button
                    className="outpatient-btn-primary"
                    onClick={handleSaveAllAndExit}
                    disabled={savingAll}
                  >
                    <i className={savingAll ? "fas fa-spinner fa-spin" : "fas fa-save"}></i>
                    {savingAll ? t('common.saving') : t('common.save')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OutpatientDetailsOverlay;

